import { useQuery } from '@tanstack/react-query';
import { NavLink, Outlet } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.js';
import { listStores } from '@/lib/api/stores.js';
import { useMultiStore } from '@/hooks/useMultiStore.js';
import { ReportFiltersProvider, useReportFilters } from '@/pages/reports/report-filters-context.js';
import type { ReportPeriod } from '@/lib/api/reports.js';

const periods: ReportPeriod[] = ['today', 'week', 'month', 'quarter', 'year', 'custom'];

function Toolbar() {
  const f = useReportFilters();
  const user = useAuthStore((s) => s.user);
  const elevated = ['tenant_admin', 'platform_admin'].includes(user?.role ?? '');
  const { isMultiStore } = useMultiStore();

  const storesQ = useQuery({
    queryKey: ['reports-stores'],
    queryFn: listStores,
    enabled: elevated && isMultiStore,
  });

  return (
    <div className="mb-6 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap gap-2">
        {periods.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => f.selectPeriod(p)}
            className={[
              'rounded-lg px-3 py-1.5 text-sm font-semibold capitalize',
              f.period === p ? 'bg-primary text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200',
            ].join(' ')}
          >
            {p === 'today'
              ? 'Today'
              : p === 'week'
                ? 'This Week'
                : p === 'month'
                  ? 'This Month'
                  : p === 'quarter'
                    ? 'This Quarter'
                    : p === 'year'
                      ? 'This Year'
                      : 'Custom'}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <label className="flex items-center gap-2">
          From
          <input
            type="date"
            className="rounded-lg border border-slate-300 px-2 py-1"
            value={f.dateFrom}
            onChange={(e) => {
              f.setPeriod('custom');
              f.setDateFrom(e.target.value);
            }}
          />
        </label>
        <label className="flex items-center gap-2">
          To
          <input
            type="date"
            className="rounded-lg border border-slate-300 px-2 py-1"
            value={f.dateTo}
            onChange={(e) => {
              f.setPeriod('custom');
              f.setDateTo(e.target.value);
            }}
          />
        </label>
      </div>
      <div className="flex flex-wrap items-center gap-4 text-sm">
        {elevated && isMultiStore ? (
          <label className="flex items-center gap-2">
            <span className="font-medium text-slate-600">Store</span>
            <select
              className="rounded-lg border border-slate-300 px-2 py-1.5"
              value={f.storeId}
              onChange={(e) => f.setStoreId(e.target.value)}
            >
              <option value="">All stores</option>
              {(storesQ.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label className="flex items-center gap-2">
          <span className="font-medium text-slate-600">Chart by</span>
          <select
            className="rounded-lg border border-slate-300 px-2 py-1.5"
            value={f.groupBy}
            onChange={(e) => f.setGroupBy(e.target.value as 'day' | 'week' | 'month')}
          >
            <option value="day">Day</option>
            <option value="week">Week</option>
            <option value="month">Month</option>
          </select>
        </label>
      </div>
    </div>
  );
}

function LayoutInner() {
  const user = useAuthStore((s) => s.user);
  const elevated = ['tenant_admin', 'platform_admin'].includes(user?.role ?? '');
  const { isMultiStore } = useMultiStore();

  const nav = [
    { to: '/reports', end: true, label: '📊 Dashboard' },
    { to: '/reports/revenue', label: '💰 Revenue' },
    { to: '/reports/outstanding-balances', label: '📋 Outstanding' },
    { to: '/reports/profit-loss', label: '📈 Profit & Loss' },
    { to: '/reports/cash-flow', label: '💵 Cash Flow' },
    { to: '/reports/tickets', label: '🎫 Tickets' },
    { to: '/reports/technicians', label: '👨‍🔧 Technicians' },
    { to: '/reports/repairs', label: '🔧 Repairs' },
    { to: '/reports/nps', label: '⭐ NPS' },
    { to: '/reports/leaderboard', label: '🏆 Leaderboard' },
    { to: '/reports/customer-lifetime-value', label: '👤 Customer LTV' },
    { to: '/reports/acquisition', label: '📣 Acquisition' },
    { to: '/reports/referrals', label: '🔗 Referrals' },
    { to: '/reports/inventory', label: '📦 Inventory' },
    { to: '/reports/parts-forecast', label: '🧮 Parts Forecast' },
    { to: '/reports/insurance', label: '🛡 Insurance Claims' },
    { to: '/reports/upsell', label: '💡 Upsell' },
    { to: '/reports/discounts', label: '🏷 Discounts' },
    { to: '/reports/tips', label: '💵 Tips' },
    { to: '/reports/walk-in-queue', label: '🚶 Walk-in Queue' },
    { to: '/reports/rma-and-rework', label: '🔁 RMA & Rework' },
    { to: '/reports/parts-requests', label: '📦 Parts Requests' },
    ...(elevated && isMultiStore ? [{ to: '/reports/multi-store', label: '🏪 Multi-Store' }] : []),
    { to: '/reports/end-of-day', label: '📋 End of Day' },
  ];

  return (
    <div className="flex min-h-[calc(100vh-6rem)] gap-6">
      <nav className="w-56 shrink-0 space-y-1 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
        {nav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              [
                'block rounded-xl px-3 py-2 text-sm font-medium',
                isActive ? 'bg-primary text-white' : 'text-slate-700 hover:bg-slate-100',
              ].join(' ')
            }
          >
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="min-w-0 flex-1">
        <Toolbar />
        <Outlet />
      </div>
    </div>
  );
}

export function ReportsLayout() {
  return (
    <ReportFiltersProvider>
      <LayoutInner />
    </ReportFiltersProvider>
  );
}
